import React, { useRef, useState } from "react";
import { Plus, Trash2, ChevronUp, ChevronDown, Type, BarChart3, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import RichEditor from "./RichEditor";
import { Field, Segmented } from "./ui";
import { ChartComposer, TickerTags, PostChart } from "./PostCharts";

/**
 * The post composer. Lived inline in `InvestorStudio` as one title field, one body
 * editor and two lists bolted underneath.
 *
 * A post is now a list of blocks the author orders. What changed and why:
 *
 * - **Blocks, not one body.** Text, a chart or an image, added anywhere and moved with
 *   the arrows. A figure can finally sit next to the sentence about it.
 * - **Excerpt and stock at the top.** They frame what you are about to write — the
 *   excerpt is the only part of a members-only post the world ever sees, and it was
 *   the second-to-last field on the form, reached after the writing was done.
 * - **Audience at the bottom.** It is the last decision, made once the post exists.
 * - **The chart for the stock you named is offered.** If the post is tagged NVDA,
 *   "chart of NVDA" is one tap; nobody should have to search for something they have
 *   already told us.
 *
 * Images are re-encoded server-side (`post_media.clean_image`), the same control the
 * avatar upload uses: the bytes are proven to be an image, EXIF is discarded and the
 * size is capped. Nothing here trusts the file the browser handed over.
 */

const MAX_IMAGE_MB = 8;

function BlockShell({ index, count, onMove, onRemove, icon: Icon, label, children, testid }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl border border-slate-200" data-testid={testid}>
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 bg-slate-50/60 rounded-t-xl">
        <Icon size={14} className="text-slate-400 shrink-0" />
        <span className="text-xs font-medium text-slate-500">{label}</span>
        <div className="ml-auto flex items-center gap-0.5">
          <button type="button" disabled={index === 0} onClick={() => onMove(index, -1)}
                  data-testid={`block-up-${index}`} aria-label={t("postEditor.moveUp")}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-brand-600 disabled:opacity-25">
            <ChevronUp size={15} />
          </button>
          <button type="button" disabled={index === count - 1} onClick={() => onMove(index, 1)}
                  data-testid={`block-down-${index}`} aria-label={t("postEditor.moveDown")}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-brand-600 disabled:opacity-25">
            <ChevronDown size={15} />
          </button>
          <button type="button" onClick={() => onRemove(index)}
                  data-testid={`block-remove-${index}`} aria-label={t("common.delete")}
                  className="p-1.5 rounded-lg text-slate-300 hover:text-rose-500">
            <Trash2 size={15} />
          </button>
        </div>
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}

export default function PostEditor({ post, onChange, onSave, onCancel }) {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(null);      // null | "chart"
  const [chartTicker, setChartTicker] = useState(null);
  const fileRef = useRef(null);

  const blocks = post.blocks || [];
  const set = (patch) => onChange({ ...post, ...patch });
  const setBlocks = (next) => set({ blocks: next });

  const addBlock = (block) => setBlocks([...blocks, block]);
  const patchBlock = (i, patch) =>
    setBlocks(blocks.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const removeBlock = (i) => setBlocks(blocks.filter((_, j) => j !== i));
  const move = (i, delta) => {
    const to = i + delta;
    if (to < 0 || to >= blocks.length) return;
    const next = [...blocks];
    [next[i], next[to]] = [next[to], next[i]];
    setBlocks(next);
  };

  const pickImage = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";                       // so the same file can be picked twice
    if (!file) return;
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      toast.error(t("postEditor.imageTooLarge", { mb: MAX_IMAGE_MB }));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => addBlock({ type: "image", src: String(reader.result),
                                     uploaded: true, alt: "", caption: "" });
    reader.onerror = () => toast.error(t("dialogs.failed"));
    reader.readAsDataURL(file);
  };

  const startChart = (ticker) => { setChartTicker(ticker); setAdding("chart"); };

  return (
    <div className="space-y-4" data-testid="post-editor">
      <h3 className="font-heading font-semibold text-xl">{post.id ? t("posts.edit") : t("posts.new")}</h3>

      <Field label={t("posts.title")}>
        <input className="input" data-testid="post-title" value={post.title || ""}
               onChange={(e) => set({ title: e.target.value })} />
      </Field>

      {/* Excerpt and stock frame the post, so they come before it. */}
      <Field label={t("posts.excerpt")}>
        <input className="input" data-testid="post-excerpt" value={post.excerpt || ""}
               placeholder={t("posts.excerptPlaceholder")}
               onChange={(e) => set({ excerpt: e.target.value })} />
        <p className="text-[11px] text-slate-400 mt-1.5">{t("posts.excerptHint")}</p>
      </Field>

      <Field label={t("postCharts.stocksLabel")}>
        <TickerTags tickers={post.tickers || []} onChange={(tk) => set({ tickers: tk })} />
        <p className="text-[11px] text-slate-400 mt-1.5">{t("postCharts.stocksHint")}</p>
      </Field>

      {/* ---------------------------- the blocks ---------------------------- */}
      <div className="space-y-3" data-testid="post-block-list">
        {blocks.map((b, i) => {
          const shell = { index: i, count: blocks.length, onMove: move, onRemove: removeBlock };
          if (b.type === "chart") {
            return (
              <BlockShell key={i} {...shell} icon={BarChart3} testid={`block-chart-${i}`}
                          label={t("postEditor.chartBlock", { ticker: b.chart?.ticker || "" })}>
                <PostChart chart={b.chart} />
              </BlockShell>
            );
          }
          if (b.type === "image") {
            return (
              <BlockShell key={i} {...shell} icon={ImageIcon} testid={`block-image-${i}`}
                          label={t("postEditor.imageBlock")}>
                <img src={b.src} alt={b.alt || ""} className="rounded-lg max-h-56 w-auto" />
                <input className="input mt-2 text-sm" data-testid={`image-alt-${i}`}
                       placeholder={t("postEditor.altPlaceholder")} value={b.alt || ""}
                       onChange={(e) => patchBlock(i, { alt: e.target.value })} />
                <p className="text-[11px] text-slate-400 mt-1">{t("postEditor.altHint")}</p>
                <input className="input mt-2 text-sm" data-testid={`image-caption-${i}`}
                       placeholder={t("postEditor.captionPlaceholder")} value={b.caption || ""}
                       onChange={(e) => patchBlock(i, { caption: e.target.value })} />
              </BlockShell>
            );
          }
          return (
            <BlockShell key={i} {...shell} icon={Type} testid={`block-text-${i}`}
                        label={t("postEditor.textBlock")}>
              <RichEditor testid={`post-text-${i}`} value={b.html || ""} minHeight={140}
                          placeholder={t("posts.bodyPlaceholder")}
                          onChange={(html) => patchBlock(i, { html })} />
            </BlockShell>
          );
        })}

        {blocks.length === 0 && (
          <p className="text-sm text-slate-400 border border-dashed border-slate-200 rounded-xl p-4 text-center">
            {t("postEditor.empty")}
          </p>
        )}
      </div>

      {/* ---------------------------- add a block ---------------------------- */}
      {adding === "chart" ? (
        <ChartComposer
          presetTicker={chartTicker}
          onKeep={(chart) => { addBlock({ type: "chart", chart }); setAdding(null); setChartTicker(null); }}
          onCancel={() => { setAdding(null); setChartTicker(null); }}
        />
      ) : (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn-outline text-sm" data-testid="add-text"
                    onClick={() => addBlock({ type: "text", html: "" })}>
              <Plus size={15} /> {t("postEditor.addText")}
            </button>
            <button type="button" className="btn-outline text-sm" data-testid="add-chart"
                    onClick={() => startChart(null)}>
              <Plus size={15} /> {t("postEditor.addChart")}
            </button>
            <button type="button" className="btn-outline text-sm" data-testid="add-image"
                    onClick={() => fileRef.current?.click()}>
              <Plus size={15} /> {t("postEditor.addImage")}
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden"
                   data-testid="image-file" onChange={pickImage} />
          </div>

          {/* You already said what this post is about. Offer it. */}
          {(post.tickers || []).length > 0 && (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <span className="text-[11px] text-slate-400">{t("postEditor.offerChart")}</span>
              {(post.tickers || []).map((tk) => (
                <button key={tk} type="button" data-testid={`add-chart-${tk}`}
                        onClick={() => startChart(tk)}
                        className="pill bg-brand-50 text-brand-700 text-xs hover:bg-brand-100">
                  <BarChart3 size={12} className="inline mr-1" />{tk}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Audience last: it is the decision you make once the post exists. */}
      <Field label={t("posts.audience")}>
        <Segmented
          options={[
            { value: "subscribers", label: t("posts.membersOnly") },
            { value: "public", label: t("posts.public") },
          ]}
          value={post.visibility} onChange={(v) => set({ visibility: v })}
          testid="post-visibility" fullWidthOnMobile
        />
        <p className="text-[11px] text-slate-400 mt-1.5">{t("posts.audienceHint")}</p>
      </Field>

      <div className="flex flex-wrap justify-end gap-2">
        <button className="btn-ghost" onClick={onCancel}>{t("common.cancel")}</button>
        <button className="btn-primary" onClick={onSave} data-testid="save-post">{t("common.save")}</button>
      </div>
    </div>
  );
}
